import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { colors, s } from "../lib/theme";

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  const load = () => api.getProfile().then(setProfile).catch((err) => setError(err.message));
  useEffect(() => { load(); }, []);

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Mi perfil</h1>
      <p style={s.subtitle}>Tu contraseña y las credenciales que se usan al publicar en el DX Cluster y al consultar indicativos.</p>

      {error && <div style={{ ...s.errorBox, marginTop: 16 }}>{error}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 20, maxWidth: 480 }}>
        {profile && <ContactCard profile={profile} onSaved={load} />}
        <PasswordCard />
        {profile && <DxClusterCard profile={profile} onSaved={load} />}
        {profile && <QrzCard profile={profile} onSaved={load} />}
        {profile && <HamqthCard profile={profile} onSaved={load} />}
      </div>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div style={s.card}>
      <div style={{ fontSize: 15, fontWeight: 700, color: colors.text, marginBottom: 4 }}>{title}</div>
      {hint && <div style={{ fontSize: 12.5, color: colors.textFaint, marginBottom: 14 }}>{hint}</div>}
      {children}
    </div>
  );
}

function ContactCard({ profile, onSaved }) {
  const [name, setName] = useState(profile.name || "");
  const [email, setEmail] = useState(profile.email || "");
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await api.updateProfile({ name, email });
      setMsg("✓ Guardado");
      onSaved();
      setTimeout(() => setMsg(null), 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Section title="Datos de contacto" hint="El email se usa para avisos de actividades e informes.">
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Field label="NOMBRE" value={name} onChange={setName} />
        <Field label="EMAIL" value={email} onChange={setEmail} type="email" />
        {error && <div style={s.errorBox}>{error}</div>}
        {msg && <div style={{ color: colors.green, fontSize: 13 }}>{msg}</div>}
        <button type="submit" style={{ ...s.btnPrimary, alignSelf: "flex-start" }}>Guardar</button>
      </form>
    </Section>
  );
}

function PasswordCard() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setOk(false);
    if (newPassword !== confirmPassword) return setError("Las dos contraseñas nuevas no coinciden");
    try {
      await api.changePassword(currentPassword, newPassword);
      setOk(true);
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Section title="Contraseña de acceso">
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Field label="ACTUAL" type="password" value={currentPassword} onChange={setCurrentPassword} />
        <Field label="NUEVA" type="password" value={newPassword} onChange={setNewPassword} />
        <Field label="REPITE LA NUEVA" type="password" value={confirmPassword} onChange={setConfirmPassword} />
        {error && <div style={s.errorBox}>{error}</div>}
        {ok && <div style={{ color: colors.green, fontSize: 13 }}>✓ Contraseña actualizada</div>}
        <button type="submit" style={{ ...s.btnPrimary, alignSelf: "flex-start" }}>Guardar contraseña</button>
      </form>
    </Section>
  );
}

function DxClusterCard({ profile, onSaved }) {
  // dxfun.com:8000 como valor por defecto, para no partir de cero — sigue
  // siendo editable si el club usa otro nodo.
  const [host, setHost] = useState(profile.dxcluster_host || "dxfun.com");
  const [port, setPort] = useState(profile.dxcluster_port || "8000");
  const [password, setPassword] = useState("");
  const [name, setName] = useState(profile.dxcluster_name || "");
  const [qth, setQth] = useState(profile.dxcluster_qth || "");
  const [locator, setLocator] = useState(profile.dxcluster_locator || "");
  const [homenode, setHomenode] = useState(profile.dxcluster_homenode || "");
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [configuring, setConfiguring] = useState(false);
  const [configureMsg, setConfigureMsg] = useState(null);
  const [configureTranscript, setConfigureTranscript] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await api.updateDxClusterCreds({ host, port, password: password || undefined, name, qth, locator, homenode });
      setPassword("");
      setMsg("✓ Guardado");
      onSaved();
      setTimeout(() => setMsg(null), 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  const configureProfile = async () => {
    setConfiguring(true);
    setConfigureMsg(null);
    try {
      const res = await api.configureDxClusterProfile();
      setConfigureTranscript(res.transcript || null);
      setConfigureMsg("✓ Perfil enviado al nodo — revisa la conversación de abajo para confirmarlo");
    } catch (err) {
      setConfigureTranscript(err.transcript || null);
      setConfigureMsg(`✗ ${err.message}`);
    } finally {
      setConfiguring(false);
    }
  };

  return (
    <Section title="DX Cluster" hint="Tu indicativo de login se usa siempre el tuyo. Nombre, QTH y locator son obligatorios: muchos nodos ignoran los spots de un usuario que no los tenga configurados.">
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Field label="SERVIDOR" value={host} onChange={setHost} placeholder="dxfun.com" />
        <Field label="PUERTO" value={port} onChange={setPort} placeholder="8000" />
        <Field
          label={profile.dxcluster_configured ? "CONTRASEÑA (configurada — deja en blanco para no cambiarla)" : "CONTRASEÑA (si el nodo la pide)"}
          type="password" value={password} onChange={setPassword}
        />
        <Field label="NOMBRE (set/name)" value={name} onChange={setName} placeholder="Tu nombre" required />
        <Field label="QTH (set/qth)" value={qth} onChange={setQth} placeholder="Tu ciudad" required />
        <Field label="LOCATOR (set/qra)" value={locator} onChange={setLocator} placeholder="IN52dr" required />
        <Field label="NODO DE REFERENCIA (set/homenode, opcional)" value={homenode} onChange={setHomenode} placeholder="p. ej. EA4RCH-5 — lo indica el propio nodo al conectar" />
        {error && <div style={s.errorBox}>{error}</div>}
        {msg && <div style={{ color: colors.green, fontSize: 13 }}>{msg}</div>}
        <button type="submit" style={{ ...s.btnPrimary, alignSelf: "flex-start" }}>Guardar</button>
      </form>

      <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${colors.lineStrong}` }}>
        <p style={{ fontSize: 11.5, color: colors.textFaint, marginBottom: 8 }}>
          Esto manda tu nombre/QTH/locator al nodo — hace falta una sola vez por nodo (no se repite en cada spot, porque mezclar
          las dos cosas es justo lo que hacía que el spot se quedara sin confirmar).
        </p>
        <button type="button" onClick={configureProfile} disabled={configuring} style={{ ...s.btnGhost, opacity: configuring ? 0.6 : 1 }}>
          {configuring ? "Enviando…" : "Configurar mi perfil en este nodo"}
        </button>
        {configureMsg && (
          <p style={{ fontSize: 12, marginTop: 8, color: configureMsg.startsWith("✓") ? colors.green : "#ffb4b4" }}>{configureMsg}</p>
        )}
        {configureTranscript && configureTranscript.length > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 12, color: colors.textFaint, cursor: "pointer" }}>Ver conversación con el nodo</summary>
            <div style={{
              marginTop: 8, padding: 10, borderRadius: 6, background: colors.panel,
              border: `1px solid ${colors.lineStrong}`, fontFamily: "monospace", fontSize: 11.5,
              maxHeight: 220, overflowY: "auto", whiteSpace: "pre-wrap",
            }}>
              {configureTranscript.map((line, i) => (
                <div key={i} style={{ color: line.dir === "enviado" ? colors.amber : line.dir === "info" ? colors.textFaint : colors.textDim, marginBottom: 3 }}>
                  [{line.dir}] {line.text}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </Section>
  );
}

function QrzCard({ profile, onSaved }) {
  const [username, setUsername] = useState(profile.qrz_username || "");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    await api.updateQrzCreds({ username, password: password || undefined });
    setPassword("");
    setMsg("✓ Guardado");
    onSaved();
    setTimeout(() => setMsg(null), 2000);
  };

  return (
    <Section title="QRZ.com" hint="Se consulta primero al escribir un indicativo en el log.">
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Field label="USUARIO" value={username} onChange={setUsername} />
        <Field
          label={profile.qrz_configured ? "CONTRASEÑA (configurada — deja en blanco para no cambiarla)" : "CONTRASEÑA"}
          type="password" value={password} onChange={setPassword}
        />
        {msg && <div style={{ color: colors.green, fontSize: 13 }}>{msg}</div>}
        <button type="submit" style={{ ...s.btnPrimary, alignSelf: "flex-start" }}>Guardar</button>
      </form>
    </Section>
  );
}

function HamqthCard({ profile, onSaved }) {
  const [username, setUsername] = useState(profile.hamqth_username || "");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    await api.updateHamqthCreds({ username, password: password || undefined });
    setPassword("");
    setMsg("✓ Guardado");
    onSaved();
    setTimeout(() => setMsg(null), 2000);
  };

  return (
    <Section title="HamQTH.com" hint="Respaldo gratuito si QRZ no devuelve el nombre (habitual con cuentas QRZ gratuitas).">
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Field label="USUARIO" value={username} onChange={setUsername} />
        <Field
          label={profile.hamqth_configured ? "CONTRASEÑA (configurada — deja en blanco para no cambiarla)" : "CONTRASEÑA"}
          type="password" value={password} onChange={setPassword}
        />
        {msg && <div style={{ color: colors.green, fontSize: 13 }}>{msg}</div>}
        <button type="submit" style={{ ...s.btnPrimary, alignSelf: "flex-start" }}>Guardar</button>
      </form>
    </Section>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required }) {
  return (
    <div>
      <label style={s.label}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        style={s.input}
        autoComplete={type === "password" ? "new-password" : "off"}
      />
    </div>
  );
}
